import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { AvianLogo } from "../components/AvianLogo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Welcome back");
      navigate(next);
    } else {
      toast.error(r.error || "Login failed");
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/11357839/pexels-photo-11357839.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900" alt="Drive" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#08153a]/85 via-[#08153a]/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="bg-white rounded-2xl inline-block p-3 self-start"><AvianLogo size={26} /></div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/70 mb-3">Welcome back</p>
            <h1 className="font-display text-4xl font-black leading-tight max-w-md">The road is calling. Sign in to keep driving.</h1>
            <div className="mt-8 inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-2 text-xs font-semibold">
              <ShieldCheck size={14} className="text-[#1d7ffd]" /> Every booking insured
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-md">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B]">tadada account</p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-[#08153a] mt-2">Log in</h2>
          <p className="text-sm text-[#64748B] mt-2">Don't have an account? <Link to="/register" className="text-[#1d7ffd] font-semibold hover:underline">Sign up</Link></p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <Field label="Email">
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#F7F8FA] border border-[#E2E8F0] rounded-xl px-4 py-3 text-sm text-[#08153a] focus:border-[#1d7ffd] outline-none" placeholder="you@example.com" />
            </Field>
            <Field label="Password">
              <div className="relative">
                <input data-testid="login-password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-[#F7F8FA] border border-[#E2E8F0] rounded-xl px-4 py-3 text-sm text-[#08153a] focus:border-[#1d7ffd] outline-none pr-12" placeholder="••••••••" />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#08153a]">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </Field>
            <button data-testid="login-submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={16} /></>)}
            </button>
          </form>

          <div className="mt-8 rounded-2xl bg-[#F7F8FA] border border-[#E2E8F0] p-4 text-xs text-[#334155]">
            <p className="font-bold text-[#08153a] mb-1.5">Demo account</p>
            <p>Email: <code className="text-[#1d7ffd]">demo@avian.co.za</code></p>
            <p>Password: <code className="text-[#1d7ffd]">Demo@Avian2026</code></p>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
